import { pointsOf, valuePoints } from "./why-it-ranks.js";

// The writes module (P1 of documentation/pr-plans/insights-simplify-plan.md, section 7, for D104; its inside
// is P3's): every write a list row or a topic makes on the Insights page goes through send(), and once a
// write saved it sends WRITTEN_EVENT, the one browser event every other open screen hears to show its
// out-of-date mark (insights/out-of-date.js).
//
// A press on a list the routes answer (TABLE_SCREENS: Improve a page and Write something new) changes the
// screen at once and saves through a small JSON route (InsightsWritesController), never through Livewire:
// the row folds or comes back, and a search intent change re-ranks the list here, from the score and
// search intent each row carries (rerank()). The change reaches the table as an answer no press of its
// own asked for (ANSWER_EVENT), which it applies in place and in silence; the route's answer then
// resolves send() in the table's own answer shape, so the table moves focus and says what happened as it
// does for a Livewire answer. A refusal or a failure puts the screen back first, then answers {error}.
// Each write's Undo is held here by the opaque token its route answered with (InsightsUndoStore), and the
// Undo sends that token back the same way.
//
// Every other write still calls the screen's own Livewire action (`livewire`), whose screen draws itself
// from Livewire: Sort my searches' buttons, the drawer, target edits, Win back clicks. Callers do not
// change; a caller that draws its own change opts into the routes by passing no Livewire call, and then
// receives the route's own answer (directWrite()).
//
// Sort my searches' other writes (a topic's name, rules, merge, delete and order, a new topic, brand
// terms) still go straight to Livewire, which answers each with ActionsView::TOPICS_CHANGED_EVENT or
// ActionsView::BRAND_TERMS_SAVED_EVENT; relay() turns those into WRITTEN_EVENT too, so every write the
// page makes reaches the mark one way.

/** The one browser event a saved write sends: {siteId, screen, action, kind} (InsightsWrites::WRITTEN_EVENT). */
export const WRITTEN_EVENT = "insights-written";

/** What a write changed, for a screen that reads only some of it. */
export const KINDS = Object.freeze({ topics: "topics", targets: "targets", brandTerms: "brand_terms" });

/** The activity table's answer that no press of its own asked for, `{table, answer}` (ActivityTable::ANSWER_EVENT). */
export const ANSWER_EVENT = "activity-table-answer";

/** The writes routes (InsightsWritesController), under /sites/{site}/insights/writes/. */
export const ROUTES = Object.freeze({ done: "done", move: "move", newTopic: "topic", topicStage: "topic-stage", undo: "undo" });

/**
 * The tables whose presses the routes save, by the activity table's id (OpportunityActivity::tableId()), with
 * the screen each is on (InsightsWriteScreen). Each row names its search in `cells.query`, exactly as stored.
 */
export const TABLE_SCREENS = Object.freeze({ "improve-searches": "improve", "write-searches": "write" });

/**
 * How much each search intent counts in the Opportunity score (config/opportunity-score.php's
 * funnel_weights, read through FunnelStage::weightOf()), which WritesModuleTest keeps equal to the config.
 */
export const FUNNEL_WEIGHTS = Object.freeze({ bottom: 1.0, middle: 0.5, not_sorted: 0.16, top: 0.04, not_relevant: 0.0 });

/** What `to` says for the built-in Not relevant topic on the move route (InsightsMoveRequest::NOT_RELEVANT). */
export const NOT_RELEVANT = "not_relevant";

/**
 * Whether a Move to topic's value is Create topic and move here's {name, stage} (D172,
 * MoveTopicChoices::newTopicOf()) rather than a topic's id or NOT_RELEVANT.
 */
export function isNewTopic(value) {
    return value !== null && typeof value === "object" && typeof value.name === "string" && value.name.trim() !== "" && typeof value.stage === "string";
}

/** A token the routes made (InsightsUndoStore::TOKEN_PATTERN), told apart from a Livewire activity's own. */
export const ROUTE_TOKEN = /^w[0-9a-f]{32}$/;

// What each action changes. Any action not named here changes topics: a search's topic, a topic's
// search intent, a set-aside search, or their Undo.
const KIND_OF = Object.freeze({
    done: KINDS.targets,
    untarget: KINDS.targets,
    target_edit: KINDS.targets,
    target_edit_undo: KINDS.targets,
    brand_term: KINDS.brandTerms,
    brand_terms: KINDS.brandTerms,
});

// A search intent set from a row's Topic part, and the value its Undo carries (ActivityTable::STAGE_ACTION).
const STAGE_ACTION = "stage";

// Move to topic from a row that stays on its list (P5's ⋯), and the value its Undo carries.
const MOVE_ACTION = "move";

// Scores closer than a billionth are a tie, as OpportunityScorer rounds them (its SCORE_DECIMALS, 9).
const TIE = 1e9;

// The query parameter a topic's Stats link names it by (InsightsLinks::TOPIC).
const TOPIC_PARAMETER = "topic";

let isRelaying = false;

// table id -> the Undos this page holds for that table's writes: folds (row id -> {token, kind, notice}),
// moves (row id -> {token, before}) and stages (topic id -> {token, before}). Kept while the page lives, so a
// table mounted again by a new answer still finds the Undos its slim rows offer.
const held = new Map();

// The server events Sort my searches already sends for every write of its own (SortMySearches::tellPage(),
// changeBrandTerms()), by ActionsView's constants.
export const RELAYED_EVENTS = Object.freeze({
    "insights-topics-changed": KINDS.topics,
    "insights-brand-terms-saved": KINDS.brandTerms,
});

/**
 * The screen an element is on: the activity whose pane holds it (data-insights-activity, its
 * ActionsView key), or "stats" for Stats. An element on neither (the Draft an article drawer, a dialog)
 * writes on the screen the reader sees behind it, which opened it.
 */
export function screenOf(el) {
    const activity = el?.closest?.("[data-insights-activity]");

    if (activity) {
        return activity.getAttribute("data-insights-activity") ?? "";
    }

    return el?.closest?.('[data-insights-view="stats"]') ? "stats" : shownScreen();
}

// The screen on show: Stats, else the activity whose pane is displayed (the others are display:none,
// which leaves them no offsetParent), else "" (the picker, or no Insights page).
function shownScreen() {
    if (typeof document === "undefined" || typeof document.querySelectorAll !== "function") {
        return "";
    }

    if (document.querySelector('[data-insights-view="stats"]')?.offsetParent) {
        return "stats";
    }

    const activity = Array.from(document.querySelectorAll("[data-insights-activity]")).find((pane) => pane.offsetParent !== null);

    return activity?.getAttribute("data-insights-activity") ?? "";
}

/** The site the page is about (data-insights-site on the page's root), or null off the Insights page. */
export function siteId() {
    if (typeof document === "undefined" || typeof document.querySelector !== "function") {
        return null;
    }

    const value = Number(document.querySelector("[data-insights-site]")?.getAttribute("data-insights-site"));

    return Number.isInteger(value) && value > 0 ? value : null;
}

// A write's answer that says it saved nothing: the activity table's {error}, or a target edit's {errors}
// by field. Every other answer (a Livewire action's return value, or nothing) is a saved write.
function isRefused(answer) {
    if (answer === null || typeof answer !== "object") {
        return false;
    }

    return (typeof answer.error === "string" && answer.error !== "") || (answer.errors !== null && typeof answer.errors === "object" && Object.keys(answer.errors).length > 0);
}

/**
 * One write, then WRITTEN_EVENT once it answered with anything but a refusal. A press the routes save
 * (routed()) has changed the screen by the time this returns its promise, and resolves with the table's
 * answer; any other calls `livewire()` (the screen's own Livewire call, a promise) and resolves with its
 * answer. Rejects as that call does, with no event: a write that failed changed nothing anywhere.
 *
 * @param {{el?: Element|null, screen?: string, action: string, payload?: object, livewire?: () => Promise<*>}} write
 *        el or screen names the screen the write was made on (screenOf()); payload is what the write is
 *        about ({table, ids, value} for a table's action, {topicId, stage} for a topic's search intent, a
 *        token for an Undo), which the routes read; no `livewire` opts into the routes (directWrite())
 */
export async function send({ el = null, screen = undefined, action, payload = {}, livewire }) {
    const route = routed({ el, screen, action, payload: payload ?? {}, livewire });
    const answer = route === null ? await livewire() : await route.write();

    if (!isRefused(answer)) {
        written({ screen: screen ?? screenOf(el), action, kind: route?.kind ?? KIND_OF[action] ?? KINDS.topics });
    }

    return answer;
}

/** The attribute a pressed button carries while its write is on its way (press()). */
export const PENDING = "data-insights-pending";

/**
 * A Blade button's press ($insightsWrite, app.js): send() with the button marked busy (PENDING, aria-busy) until the
 * write answers, and any press of it meanwhile ignored. A Livewire write is a round trip that redraws its screen; with
 * nothing to show it was heard, a person presses again, and the second press of an Undo finds its change already put
 * back ("This change can no longer be undone here"), or a second Move finds its dialog's rows gone.
 */
export async function press(el, write) {
    if (el?.hasAttribute?.(PENDING)) {
        return null;
    }

    el?.setAttribute?.(PENDING, "");
    el?.setAttribute?.("aria-busy", "true");

    try {
        return await send({ el, ...write });
    } finally {
        el?.removeAttribute?.(PENDING);
        el?.removeAttribute?.("aria-busy");
    }
}

/** Tells every screen that a write on `screen` saved. */
export function written({ screen = "", action = "", kind = KIND_OF[action] ?? KINDS.topics }) {
    if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
        return;
    }

    window.dispatchEvent(new CustomEvent(WRITTEN_EVENT, { detail: { siteId: siteId(), screen, action, kind } }));
}

/**
 * Relays the server events Sort my searches sends for its Livewire writes (RELAYED_EVENTS) as
 * WRITTEN_EVENT, once per page. Sort is their only sender, so Sort is the screen that wrote; the guide's
 * brand terms come before any screen has read anything.
 */
export function relay() {
    if (isRelaying || typeof window === "undefined" || typeof window.addEventListener !== "function") {
        return;
    }

    isRelaying = true;

    Object.entries(RELAYED_EVENTS).forEach(([name, kind]) => {
        window.addEventListener(name, () => written({ screen: "sort", action: name, kind }));
    });
}

// ----------------------------------------------------------------------------------------- routing

// The route a write takes, as {kind, write}, or null for the screen's own Livewire call: a press on a list
// the routes answer, an Undo whose token the routes made, or a caller that passed no Livewire call.
function routed({ el, screen, action, payload, livewire }) {
    const site = siteId();

    if (site === null || typeof fetch !== "function") {
        return null;
    }

    const table = tableWrite({ el, action, payload, site });

    if (table !== null) {
        return table;
    }

    if (action === "undo_token" && ROUTE_TOKEN.test(String(payload.token ?? ""))) {
        return { kind: KINDS.topics, write: () => direct(site, ROUTES.undo, { token: payload.token }) };
    }

    return typeof livewire === "function" ? null : directWrite({ el, screen, action, payload, site });
}

// A caller that draws its own change (no Livewire call): the route's answer as it comes, {error} on a
// refusal or a failure, in the route's words where it gave some.
function directWrite({ el, screen, action, payload, site }) {
    const on = payload.screen ?? screen ?? screenOf(el);

    switch (action) {
        case "done":
            return { kind: KINDS.targets, write: () => direct(site, ROUTES.done, { screen: on, searches: payload.searches }) };
        case MOVE_ACTION:
        case "not_relevant":
            return { kind: KINDS.topics, write: () => direct(site, ROUTES.move, { screen: on, to: action === "not_relevant" ? NOT_RELEVANT : payload.to, searches: payload.searches }) };
        case "topic_stage":
            return { kind: KINDS.topics, write: () => direct(site, ROUTES.topicStage, { screen: on, topicId: payload.topicId, stage: payload.stage ?? null }) };
        default:
            throw new Error(`The writes routes do not save "${action}"; pass its Livewire call.`);
    }
}

async function direct(site, route, body) {
    try {
        return await post(site, route, body);
    } catch (failure) {
        return { error: failure.words ?? "" };
    }
}

// A press on a table the routes answer (TABLE_SCREENS), or null for one they do not, which keeps its
// Livewire call: another table, a row with no search to name, an Undo this page holds no token for (a
// fold the activity made itself, such as a copy's in the drawer), or an action the routes do not save.
function tableWrite({ el, action, payload, site }) {
    const screen = TABLE_SCREENS[payload.table];
    const data = screen === undefined ? null : tableData(el, payload.table);

    if (data === null || !Array.isArray(payload.ids) || payload.ids.length === 0) {
        return null;
    }

    const rows = payload.ids.map((id) => rowOf(data, id));

    if (rows.some((row) => row === null || queryOf(row) === null)) {
        return null;
    }

    const press = { data, table: payload.table, screen, site, rows };

    switch (action) {
        case "done":
            return { kind: KINDS.targets, write: () => fold(press, "done") };
        case "not_relevant":
            return { kind: KINDS.topics, write: () => fold(press, "not_relevant") };
        case MOVE_ACTION:
            if (isNewTopic(payload.value)) {
                return { kind: KINDS.topics, write: () => moveRows(press, null, payload.value) };
            }

            return payload.value === NOT_RELEVANT ? { kind: KINDS.topics, write: () => fold(press, "not_relevant") } : moveTo(press, payload.value);
        case STAGE_ACTION:
            return rows[0].topicId > 0 ? { kind: KINDS.topics, write: () => setStage(press, payload.value ?? null) } : null;
        case "undo":
            return undoOf(press, payload.value);
        default:
            return null;
    }
}

// --------------------------------------------------------------------------------------- the writes

// Done or Not relevant: the rows fold at once, with no Undo until the route has answered with its token.
async function fold(press, kind) {
    const { data, table, screen, site, rows } = press;
    const ids = rows.map((row) => row.id);

    tell(table, { finished: { ids, kind: foldKind(screen, kind), notice: "", undo: false } });

    let answer;

    try {
        answer = kind === "done"
            ? await post(site, ROUTES.done, { screen, searches: rows.map((row) => ({ query: queryOf(row), advice: row.advice ?? null })) })
            : await post(site, ROUTES.move, { screen, to: NOT_RELEVANT, searches: rows.map((row) => ({ query: queryOf(row), isUnsafe: row.isUnsafe === true })) });
    } catch (failure) {
        tell(table, { restored: ids });

        return { error: failure.words ?? failedWords(data) };
    }

    // By the place each search had in the press: the route answers each one it saved, with its Undo's token.
    const saved = new Map((kind === "done" ? answer.done : answer.moved).map((item) => [item.index, item.undo]));
    const folds = holding(table).folds;
    const undo = [];

    rows.forEach((row, index) => {
        if (typeof saved.get(index) === "string") {
            folds.set(row.id, { token: saved.get(index), kind, notice: answer.notice });
            undo.push(row.id);
        }
    });

    return {
        finished: { ids: rows.filter((row, index) => saved.has(index)).map((row) => row.id), kind: answer.kind ?? undefined, notice: answer.notice, undo },
        restored: rows.filter((row, index) => !saved.has(index)).map((row) => row.id),
        announce: answer.announce,
    };
}

// Move to topic from a row that stays on its list (P5's ⋯): the rows take the topic, and its search intent
// as far as this list already knows it, and the list re-ranks; the route's answer names the search intent.
function moveTo(press, to) {
    const topicId = Number(to);

    if (!Number.isInteger(topicId) || topicId <= 0) {
        return null;
    }

    return { kind: KINDS.topics, write: () => moveRows(press, topicId) };
}

// Create topic and move here (D172) is the same move into a topic the route makes first, `newTopic` being its
// {name, stage}: no row knows that topic yet, so the rows take their place once the route has answered, and every
// row the press moved answers one Undo token, which puts back the whole press.
async function moveRows(press, topicId, newTopic = null) {
    const { data, table, screen, site, rows } = press;
    const known = newTopic === null ? data.rows.find((row) => row.topicId === topicId && row.detail?.topic) : null;
    const before = new Map(rows.map((row) => [row.id, placeOf(row)]));
    const searches = rows.map((row) => ({ query: queryOf(row), isUnsafe: row.isUnsafe === true }));

    if (known) {
        tell(table, { replaced: rerank(data, { rows: Object.fromEntries(rows.map((row) => [row.id, placeOf(known)])) }) });
    }

    let answer;

    try {
        answer = newTopic === null
            ? await post(site, ROUTES.move, { screen, to: topicId, searches })
            : await post(site, ROUTES.newTopic, { screen, name: newTopic.name, stage: newTopic.stage, searches });
    } catch (failure) {
        tell(table, { replaced: rerank(data, { rows: Object.fromEntries(before) }) });

        return { error: failure.words ?? failedWords(data) };
    }

    const saved = new Map(answer.moved.map((item) => [item.index, item.undo]));
    const ids = rows.filter((row, index) => saved.has(index)).map((row) => row.id);

    // Moved into Not relevant by its id, or into a topic whose search intent is Not relevant: no list holds
    // such a search (D47), so the rows leave the list as a set-aside folds them, and each fold's notice
    // offers its Undo, which a notice looks for among the folds (undoOf()).
    if (answer.topic.isNotRelevant || answer.topic.stage === NOT_RELEVANT) {
        rows.forEach((row, index) => {
            if (saved.has(index)) {
                holding(table).folds.set(row.id, { token: saved.get(index), kind: NOT_RELEVANT, notice: answer.notice });
            }
        });

        return { finished: { ids, kind: answer.kind ?? foldKind(screen, NOT_RELEVANT), notice: answer.notice, undo: ids }, announce: answer.announce };
    }

    rows.forEach((row, index) => {
        if (saved.has(index)) {
            holding(table).moves.set(row.id, { token: saved.get(index), before: before.get(row.id) });
        }
    });

    const place = { topicId: answer.topic.id, stage: answer.topic.stage, isSuggested: answer.topic.isSuggested, topic: { id: answer.topic.id, name: answer.topic.name } };
    const places = Object.fromEntries(rows.map((row) => [row.id, saved.has(rows.indexOf(row)) ? place : before.get(row.id)]));

    // The rows stay on the list, so the move's Undo is on its toast (ActivityTable's toastUndo, P5, D101),
    // as the activity's own Move to topic answers it; the toast's Undo comes back as `undo` with `move`.
    return { replaced: rerank(data, { rows: places }), announce: answer.announce, ...(ids.length > 0 ? { toastUndo: { kind: MOVE_ACTION, ids } } : {}) };
}

// A topic's search intent from a row's Topic part: every row of the topic takes it and the list re-ranks.
async function setStage(press, stage) {
    const { data, table, screen, site, rows } = press;
    const topicId = rows[0].topicId;
    const before = placeOf(rows[0]);

    tell(table, { replaced: rerank(data, { topics: { [topicId]: { stage, isSuggested: false } } }) });

    let answer;

    try {
        answer = await post(site, ROUTES.topicStage, { screen, topicId, stage });
    } catch (failure) {
        tell(table, { replaced: rerank(data, { topics: { [topicId]: before } }) });

        return { error: failure.words ?? failedWords(data) };
    }

    holding(table).stages.set(topicId, { token: answer.undo, before });

    return {
        replaced: rerank(data, { topics: { [topicId]: { stage: answer.topic.stage, isSuggested: answer.topic.isSuggested } } }),
        announce: answer.announce,
        stageUndo: { rowId: rows[0].id, topicId, isOffered: true },
    };
}

// An Undo of a write this page made through the routes, or null for one it did not, which the activity holds.
function undoOf(press, value) {
    const { table, rows } = press;
    const row = rows[0];
    const holds = holding(table);

    if (value === STAGE_ACTION) {
        return holds.stages.has(row.topicId) ? { kind: KINDS.topics, write: () => undoStage(press, holds.stages.get(row.topicId)) } : null;
    }

    // The toast's Undo names every row the move moved (P5's bulk Move to topic), so each that holds one goes back.
    if (value === MOVE_ACTION) {
        const moved = rows.filter((candidate) => holds.moves.has(candidate.id));

        return moved.length > 0 ? { kind: KINDS.topics, write: () => undoMoves({ ...press, rows: moved }) } : null;
    }

    const kept = holds.folds.get(row.id);

    return kept ? { kind: kept.kind === "done" ? KINDS.targets : KINDS.topics, write: () => undoFold(press, kept) } : null;
}

// A fold's Undo puts back the write its token holds, and every row folded by the same press comes back with it:
// one token for several rows is Create topic and move here into Not relevant (D172), which one Undo undoes whole.
async function undoFold(press, kept) {
    const { data, table, screen, site, rows } = press;
    const folds = holding(table).folds;
    const ids = [rows[0].id, ...Array.from(folds.entries()).filter(([id, fold]) => id !== rows[0].id && fold.token === kept.token).map(([id]) => id)];

    tell(table, { restored: ids });

    try {
        const answer = await post(site, ROUTES.undo, { token: kept.token });

        ids.forEach((id) => folds.delete(id));

        return { restored: ids, announce: answer.announce };
    } catch (failure) {
        // Gone (used, or kept past its time) can never be pressed again; anything else keeps its Undo.
        const isGone = failure.status === 410;

        if (isGone) {
            ids.forEach((id) => folds.delete(id));
        }

        tell(table, { finished: { ids, kind: foldKind(screen, kept.kind), notice: kept.notice, undo: !isGone } });

        return { error: failure.words ?? failedWords(data) };
    }
}

async function undoStage(press, stage) {
    const { data, table, site, rows } = press;
    const topicId = rows[0].topicId;
    const after = placeOf(rows[0]);

    tell(table, { replaced: rerank(data, { topics: { [topicId]: stage.before } }), stageUndo: { topicId, isOffered: false } });

    let answer;

    try {
        answer = await post(site, ROUTES.undo, { token: stage.token });
    } catch (failure) {
        if (failure.status === 410) {
            holding(table).stages.delete(topicId);
        }

        tell(table, { replaced: rerank(data, { topics: { [topicId]: after } }), stageUndo: { topicId, isOffered: failure.status !== 410 } });

        return { error: failure.words ?? failedWords(data) };
    }

    holding(table).stages.delete(topicId);

    const now = answer.topic ? { stage: answer.topic.stage, isSuggested: answer.topic.isSuggested } : stage.before;

    return { replaced: rerank(data, { topics: { [topicId]: now } }), announce: answer.announce, stageUndo: { rowId: rows[0].id, topicId, isOffered: false } };
}

// Every row of a Move to topic goes back to where it was at once, then each Undo is sent once (InsightsWriter::move()
// keeps one a search; Create topic and move here one a press, D172), one after another: each takes the site's lock
// in turn, and a select-all's move must not send hundreds at once. A row whose Undo is refused goes back to its new
// place, and keeps that Undo unless it is gone (410).
async function undoMoves(press) {
    const { data, table, site, rows } = press;
    const moves = holding(table).moves;
    const undos = rows.map((row) => ({ id: row.id, move: moves.get(row.id), after: placeOf(row) }));
    const places = Object.fromEntries(undos.map(({ id, move }) => [id, move.before]));
    const byToken = new Map();
    let announce;
    let failure = null;

    undos.forEach((undo) => byToken.set(undo.move.token, [...(byToken.get(undo.move.token) ?? []), undo]));
    tell(table, { replaced: rerank(data, { rows: places }) });

    for (const [token, sharing] of byToken) {
        try {
            const answer = await post(site, ROUTES.undo, { token });

            sharing.forEach(({ id }) => moves.delete(id));
            announce ??= answer.announce;
        } catch (refused) {
            sharing.forEach(({ id, after }) => {
                if (refused.status === 410) {
                    moves.delete(id);
                }

                places[id] = after;
            });
            failure ??= refused;
        }
    }

    if (failure !== null) {
        tell(table, { replaced: rerank(data, { rows: places }) });

        return { error: failure.words ?? failedWords(data) };
    }

    return { replaced: rerank(data, { rows: places }), announce };
}

// ---------------------------------------------------------------------------------- re-ranking (D104)

/**
 * The table's rows with search intents and topics changed, in the order the Opportunity score ranks them
 * (OpportunityScorer::buyersFirst(), D130: the most ready-to-buy search intent first, then highest score first,
 * ties to more Potential clicks, then more impressions, then the search). A changed row's score is its own with
 * points with its Value boost swapped where it crosses Bottom of funnel, the one search intent Value counts at
 * (why-it-ranks.js valuePoints(), D147): the score sums its boosts, so that is the score the scorer would give
 * it. Rows folded into a notice
 * keep their places, as a list ranked again keeps them (D70), and a table whose rows carry no score keeps
 * its order. Visitor quality, the one other factor a topic decides, stays as each row carries it until the
 * list reads again, and so do the words of Why it ranks.
 *
 * @param {object} data the table's state (insights-activity-table.js)
 * @param {{topics?: Object<number, {stage: ?string, isSuggested: boolean}>, rows?: Object<string, object>}} changes
 *        by topic id, a search intent for every row of the topic; by row id, a row's whole place (placeOf())
 * @returns {object[]} the rows, changed rows copied, as the table's `replaced` answer takes them
 */
export function rerank(data, changes) {
    const labels = Array.isArray(data.table?.stages) ? data.table.stages : [];
    const rows = data.rows.map((row) => {
        const place = changes.rows?.[row.id] ?? (changes.topics?.[row.topicId] ? { ...placeOf(row), ...changes.topics[row.topicId] } : null);

        return place ? placed(row, place, labels) : row;
    });

    if (!rows.every((row) => typeof row.rawScore?.value === "number")) {
        return rows;
    }

    const isFolded = (row) => Boolean(data.notices?.[row.id]);
    // Each row's sort keys worked out once, not once a comparison: a full list is 500 rows.
    const open = rows
        .filter((row) => !isFolded(row))
        .map((row) => ({ row, weight: weightOf(row.stage), score: Math.round(row.rawScore.value * TIE) / TIE, potential: cellOf(row, "potential"), impressions: cellOf(row, "impressions"), query: queryOf(row) ?? "" }))
        .sort(byBuyersFirst)
        .map((keyed) => keyed.row);
    let next = 0;

    return rows.map((row) => (isFolded(row) ? row : open[next++]));
}

/**
 * A row's place: its topic and that topic's search intent, as the routes answer them. The row's Topic part names
 * the topic's own (D137), which differs from the row's for a search counted as Middle of funnel for holding no
 * buying word.
 */
function placeOf(row) {
    return {
        topicId: row.topicId ?? 0,
        stage: row.detail?.topic ? (row.detail.topic.stage ?? null) : (row.stage ?? null),
        isSuggested: row.detail?.topic?.isSuggested === true,
        topic: row.detail?.topic ? { id: row.detail.topic.id, name: row.detail.topic.name } : null,
    };
}

// A row in its new place: the search intent it counts under there, the score that intent weights (and its Value,
// at Bottom of funnel only), and the words that name it. A search in a Bottom of funnel topic counts as Bottom of
// funnel only when it holds one of the site's buying words, as every list counts it (BuyingWords::searchStage(), D137);
// the row says whether it holds one, null for a site with no list yet, which counts by its topic, and its Topic part
// keeps naming the topic's own search intent.
function placed(row, place, labels) {
    const topicStage = place.stage ?? null;
    const stage = topicStage === "bottom" && row.holdsBuyingWord === false ? "middle" : topicStage;
    // The score in points (D147): only Value's boost follows the search intent, at Bottom of funnel alone (D130).
    const factors = Array.isArray(row.detail?.factors)
        ? row.detail.factors.map((factor) => (factor?.key === "value" ? withPoints(factor, valuePoints(factor, stage), stage) : factor))
        : null;
    const raw = factors !== null && typeof row.rawScore?.value === "number" ? pointsOf(factors) : null;
    const label = labels.find((choice) => (choice.value ?? null) === stage)?.label;
    const topicLabel = labels.find((choice) => (choice.value ?? null) === topicStage)?.label;
    const topicId = place.topicId ?? row.topicId;
    const detail = row.detail && typeof row.detail === "object" ? row.detail : null;

    return {
        ...row,
        stage,
        topicId,
        rawScore: raw === null ? row.rawScore : { ...row.rawScore, value: raw, display: String(raw) },
        cells: {
            ...row.cells,
            ...(row.cells?.stage && label !== undefined ? { stage: { ...row.cells.stage, value: stage, display: label } } : {}),
            // The Opportunity column shows the points themselves: nothing scales them (D147).
            ...(raw !== null && row.cells?.score ? { score: { ...row.cells.score, value: raw, display: String(raw) } } : {}),
        },
        detail: detail === null
            ? row.detail
            : {
                  ...detail,
                  topic: detail.topic
                      ? {
                            ...detail.topic,
                            ...(place.topic ?? {}),
                            stage: topicStage,
                            isSuggested: place.isSuggested === true,
                            ...(topicLabel === undefined ? {} : { stageLabel: topicLabel }),
                            url: topicId === row.topicId ? detail.topic.url : withTopic(detail.topic.url, topicId),
                        }
                      : detail.topic,
                  factors: factors ?? detail.factors,
              },
    };
}

// Value's boost with a new figure, written as the server writes it (OpportunityActivity::factors()): its points,
// its fact, plain words and short value for the search intent (a buyer's cost per click, or that it counts for
// buyers only), the step it marks on its scale (a buyer's, or none), and its gauge.
function withPoints(factor, points, stage) {
    const isBuyer = (stage ?? null) === "bottom";
    const fact = isBuyer ? factor.buyerFact ?? factor.fact : factor.otherFact ?? factor.fact;
    const plain = isBuyer ? factor.buyerPlain ?? factor.plain : factor.otherPlain ?? factor.plain;
    const short = isBuyer ? factor.buyerShort ?? factor.short : factor.otherShort ?? factor.short;
    const step = isBuyer ? factor.buyerStep ?? null : null;

    return { ...factor, points, value: `+${points}`, fact, plain, short, step, gauge: factor.gauge ? { ...factor.gauge, value: points } : factor.gauge };
}

function weightOf(stage) {
    return FUNNEL_WEIGHTS[stage ?? "not_sorted"] ?? FUNNEL_WEIGHTS.not_sorted;
}



// The scorer's order over rows keyed by rerank() (OpportunityScorer::buyersFirst()): the most ready-to-buy search
// intent first, then score, then Potential clicks, impressions and the search.
function byBuyersFirst(a, b) {
    return b.weight - a.weight || b.score - a.score || b.potential - a.potential || b.impressions - a.impressions || (a.query < b.query ? -1 : a.query > b.query ? 1 : 0);
}

function cellOf(row, key) {
    const value = row.cells?.[key]?.value;

    return typeof value === "number" ? value : 0;
}

// A topic's Stats link (InsightsLinks::topicInStats()) naming another topic; none when it named none.
function withTopic(url, topicId) {
    if (typeof url !== "string" || url === "") {
        return url ?? null;
    }

    const [path, query = ""] = url.split("?");
    const parameters = new URLSearchParams(query);

    parameters.set(TOPIC_PARAMETER, String(topicId));

    return `${path}?${parameters.toString()}`;
}

/** A number in the notation `display` was written in (the workspace's separators), with `decimals` places. */
export function formatLike(display, value, decimals) {
    const text = String(display ?? "");
    const decimal = text.match(/([.,])\d+$/)?.[1] ?? ".";
    const group = decimal === "," ? "." : ",";
    const [whole, fraction] = Math.abs(value).toFixed(decimals).split(".");

    return (value < 0 ? "-" : "") + whole.replace(/\B(?=(\d{3})+(?!\d))/g, group) + (fraction ? decimal + fraction : "");
}

// ------------------------------------------------------------------------------------------- helpers

// The table's own state, found from the element that pressed (its root): Alpine's data for it.
function tableData(el, id) {
    const alpine = typeof window === "undefined" ? null : window.Alpine;
    const data = el && alpine && typeof alpine.$data === "function" ? alpine.$data(el) : null;

    return data && Array.isArray(data.rows) && data.table?.id === id ? data : null;
}

function rowOf(data, id) {
    return data.rows.find((row) => row?.id === id) ?? null;
}

// A row's search, exactly as stored (the activity's `cells.query`), or null for a row that names none.
function queryOf(row) {
    const query = row?.cells?.query?.value;

    return typeof query === "string" && query !== "" ? query : null;
}

// A card table's fold names its kind, so its slim row reads that kind's sentence (ActivityTable::FOLDED_KINDS).
function foldKind(screen, kind) {
    return screen === "improve" || screen === "write" ? kind : undefined;
}

function failedWords(data) {
    return typeof data.strings?.failed === "string" ? data.strings.failed : "";
}

function holding(table) {
    if (!held.has(table)) {
        held.set(table, { folds: new Map(), moves: new Map(), stages: new Map() });
    }

    return held.get(table);
}

// The table hears it as an answer no press asked for, and applies it in place and in silence.
function tell(table, answer) {
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
        window.dispatchEvent(new CustomEvent(ANSWER_EVENT, { detail: { table, answer } }));
    }
}

// The route's answer, or a WriteRefused with its words (the answer's `error`) and its status. A redirect
// (a gate in the page's own middleware sending the press elsewhere) or a body that is not JSON fails too.
async function post(site, route, body) {
    let response;

    try {
        response = await fetch(`/sites/${site}/insights/writes/${route}`, {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json", Accept: "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-TOKEN": csrfToken() },
            body: JSON.stringify(body),
        });
    } catch {
        throw new WriteRefused(null, 0);
    }

    let answer = null;

    try {
        answer = await response.json();
    } catch {
        answer = null;
    }

    if (!response.ok || response.redirected || answer === null || typeof answer !== "object") {
        throw new WriteRefused(typeof answer?.error === "string" && answer.error !== "" ? answer.error : null, response.status);
    }

    return answer;
}

class WriteRefused extends Error {
    constructor(words, status) {
        super(words ?? "The write did not save.");
        this.words = words;
        this.status = status;
    }
}

function csrfToken() {
    return typeof document === "undefined" || typeof document.querySelector !== "function"
        ? ""
        : document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}
